import { type RoomMembersQuery, type RoomRequestsQuery, type RoomSummaryQuery } from "@crowdcoursing/api/graphql";
import { type PfpRoomPurpose } from "@crowdcoursing/api/openapi";
import { Box, Grid } from "@mui/material";

// User-facing strings
const STRINGS = {
  JOIN_ROOM_PRIVATE: "Request to join this room to see schedules, activities, and more.",
  JOIN_ROOM_PUBLIC: "Join this room to see schedules, activities, and more.",
  JOIN_ROOM_TITLE: "Join Room to View Details",
} as const;

import { BlurOverlay } from "~ui/molecules/BlurOverlay/BlurOverlay";
import { PopOutDescription } from "~ui/molecules/PopOutDescription/PopOutDescription";
import { type Milestone, type Task, TasksMilestonesCard } from "~ui/molecules/TasksMilestonesCard/TasksMilestonesCard";
import { UpcomingEventsCard } from "~ui/molecules/UpcomingEventsCard/UpcomingEventsCard";
import { ChatSlideout } from "~ui/organisms/ChatSlideout/ChatSlideout";
import { EventsSlideout } from "~ui/organisms/EventsSlideout/EventsSlideout";
import { MembersSlideout } from "~ui/organisms/MembersSlideout/MembersSlideout";
import { ResourcesSlideout } from "~ui/organisms/ResourcesSlideout/ResourcesSlideout";
import { RoomActivityCards } from "~ui/organisms/RoomActivityCards/RoomActivityCards";
import { RoomHeader } from "~ui/organisms/RoomHeader/RoomHeader";

// Extract the specific room type from the query result
type RoomFromQuery = NonNullable<RoomSummaryQuery["room"]>;

// Extract member types from the separate RoomMembersQuery and RoomRequestsQuery
type MemberFromQuery = NonNullable<RoomMembersQuery["room"]>["members"][0] & {
  isFacilitator?: boolean; // Add derived field
  isOwner?: boolean; // Add derived field
};

type RequestFromQuery = NonNullable<RoomRequestsQuery["room"]>["requests"][0];

export interface RoomDashboardTemplateProps {
  room: RoomFromQuery;
  imageUrl?: string;
  status?: "idle" | "loading" | "error" | "success";
  onImageChange?: (file: File) => void;
  onJoinRoom?: () => Promise<void>;
  onLeaveRoom?: () => Promise<void>;
  onCancelRequest?: () => Promise<void>;
  onAddMember?: (member: { firstName: string; lastName: string; email: string }) => void;
  onEditRoom?: () => void;
  onDeleteRoom?: () => void;
  onShareRoom?: () => void;
  // Member management props - using exact GraphQL types
  members?: MemberFromQuery[];
  requests?: RequestFromQuery[];
  onRemoveMember?: (memberId: string) => void;
  onApproveMember?: (memberId: string) => void;
  onRejectMember?: (memberId: string) => void;
  // Owner-specific member management props
  onAddFacilitator?: (memberId: string) => void;
  onRemoveFacilitator?: (memberId: string) => void;
  onAddOwner?: (memberId: string) => void;
  // Resource management props
  onAddResource?: (resource: { name: string; description: string; url: string }) => void;
  onEditResource?: (
    resource: { name: string; description: string; url: string },
    oldResource: { id: string; name: string; description: string; url: string },
  ) => void;
  onDeleteResource?: (resource: { id: string; name: string; description: string; url: string }) => void;
  // Task management props
  onAddTask?: (task: { name: string; description: string; dueDate: Date; userId?: string }) => void;
  onDeleteTask?: (task: Task) => void;
  onCompleteTask?: (task: Task) => void;
  // Milestone management props
  onAddMilestone?: (milestone: { name: string; description: string; dueDate: Date }) => void;
  onDeleteMilestone?: (milestone: Milestone) => void;
  onCompleteMilestone?: (milestone: Milestone) => void;
  // Event management props
  onAddEvent?: (event: {
    bubbleId: string;
    description: string;
    isAutoJoin: boolean;
    isSearchable: boolean;
    name: string;
  }) => void;
  bubbleSelectItems?: { id: string; value: string }[];
  defaultBubbleId?: string;
  canCreateEvents?: boolean;
  // Chat props
  chatMemberCount?: number;
  // Slideout state
  resourcesSlideoutOpen: boolean;
  membersSlideoutOpen: boolean;
  chatSlideoutOpen: boolean;
  eventsSlideoutOpen: boolean;
  onResourcesClose: () => void;
  onMembersClose: () => void;
  onChatClose: () => void;
  onEventsClose: () => void;
  onResourcesClick: () => void;
  onMembersClick: () => void;
  onChatClick: () => void;
  onEventsClick: () => void;
  // Room update props
  onUpdateRoom?: (values: {
    bubbleId: string;
    description: string;
    isPublic: boolean;
    isSearchable: boolean;
    name: string;
  }) => void;
  timeExceptionsHref?: string;
  supervisorHref?: string;
  worksiteAdminsHref?: string;
  pfpRoomPurpose?: PfpRoomPurpose | null;
}

export const RoomDashboardTemplate = ({
  room,
  imageUrl,
  status,
  onImageChange,
  onJoinRoom,
  onLeaveRoom,
  onCancelRequest,
  onAddMember,
  members = [],
  requests = [],
  onRemoveMember,
  onApproveMember,
  onRejectMember,
  onAddFacilitator,
  onRemoveFacilitator,
  onAddOwner,
  onAddResource,
  onEditResource,
  onDeleteResource,
  onAddTask,
  onDeleteTask,
  onCompleteTask,
  onAddMilestone,
  onDeleteMilestone,
  onCompleteMilestone,
  onAddEvent,
  bubbleSelectItems = [],
  defaultBubbleId,
  canCreateEvents = true,
  chatMemberCount,
  resourcesSlideoutOpen,
  membersSlideoutOpen,
  chatSlideoutOpen,
  eventsSlideoutOpen,
  onResourcesClose,
  onMembersClose,
  onChatClose,
  onEventsClose,
  onResourcesClick,
  onMembersClick,
  onChatClick,
  onEventsClick,
  onUpdateRoom,
  onDeleteRoom,
  timeExceptionsHref,
  supervisorHref,
  worksiteAdminsHref,
  pfpRoomPurpose,
}: RoomDashboardTemplateProps) => {
  const events = room.sessions || [];
  const resources = room.resources || [];

  // Check if user can manage resources (facilitator or owner)
  const canManageResources = room.userIsFacilitator || room.userIsOwner;

  // Check if user can manage members (facilitator or owner)
  const canManageMembers = room.userIsFacilitator || room.userIsOwner;

  // Check if user can see member status (facilitator or owner)
  const canSeeMemberStatus = room.userIsFacilitator || room.userIsOwner;

  // Calculate member count from provided data - only include requests if user can see them
  const memberCount = members.length;

  // Handle room update by adding bubbleId
  const handleUpdateRoom = (values: {
    description: string;
    isPublic: boolean;
    isSearchable: boolean;
    name: string;
  }) => {
    if (onUpdateRoom) {
      onUpdateRoom({
        ...values,
        bubbleId: room.bubbleId || "",
      });
    }
  };

  // Transform members data for the header
  const headerMembers = members.map((member) => ({
    email: member.email || "",
    id: member.id,
    isFacilitator: member.isFacilitator || false,
    name: `${member.firstName} ${member.lastName}`,
    roles: member.roles,
  }));

  return (
    <Box>
      {/* Room Header and Description - Side by side on XL screens */}
      <Box sx={{ display: "flex", flexDirection: { xl: "row", xs: "column" }, gap: 3, mb: 3 }}>
        <Box sx={{ flex: "1 1 50%" }}>
          {/* Room Header */}
          <RoomHeader
            room={room}
            members={headerMembers}
            imageUrl={imageUrl}
            status={status}
            onImageChange={onImageChange}
            onJoinRoom={onJoinRoom}
            onLeaveRoom={onLeaveRoom}
            onCancelRequest={onCancelRequest}
            onUpdateRoom={handleUpdateRoom}
            onDeleteRoom={onDeleteRoom}
            bubbleSelectItems={bubbleSelectItems}
            timeExceptionsHref={timeExceptionsHref}
            supervisorHref={supervisorHref}
            worksiteAdminsHref={worksiteAdminsHref}
            pfpRoomPurpose={pfpRoomPurpose}
            sx={{ height: "100%", mb: { xl: 0, xs: 0 } }}
          />
        </Box>
        <Box sx={{ flex: "1 1 50%" }}>
          {/* Room Description with Pop-out Dialog */}
          <PopOutDescription description={room.description} />
        </Box>
      </Box>
      {/* Main Content with Blur and Overlay */}
      <BlurOverlay
        isMember={room.userIsMember || room.userIsOwner}
        isPublic={room.isPublic}
        showOverlayText
        overlayStrings={{
          joinPrivate: STRINGS.JOIN_ROOM_PRIVATE,
          joinPublic: STRINGS.JOIN_ROOM_PUBLIC,
          joinTitle: STRINGS.JOIN_ROOM_TITLE,
        }}
      >
        {/* Quick Stats */}
        <RoomActivityCards
          memberCount={memberCount}
          eventCount={events.length}
          resourceCount={resources.length}
          chatMemberCount={chatMemberCount || 0}
          onMembersClick={onMembersClick}
          onEventsClick={onEventsClick}
          onResourcesClick={onResourcesClick}
          onChatClick={onChatClick}
        />

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Tasks & Milestones */}
          <Grid
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <TasksMilestonesCard
              tasks={room.roomTasks || []}
              milestones={room.roomMilestones || []}
              canManage={room.userIsFacilitator || room.userIsOwner}
              onAddTask={onAddTask}
              onDeleteTask={onDeleteTask}
              onCompleteTask={onCompleteTask}
              onAddMilestone={onAddMilestone}
              onDeleteMilestone={onDeleteMilestone}
              onCompleteMilestone={onCompleteMilestone}
              members={members.map((member) => ({
                email: member.email || undefined,
                firstName: member.firstName || "",
                id: member.id,
                lastName: member.lastName || "",
              }))}
            />
          </Grid>

          {/* Upcoming Events */}
          <Grid
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <UpcomingEventsCard room={room} />
          </Grid>
        </Grid>
      </BlurOverlay>
      {/* Slideouts */}
      <ResourcesSlideout
        open={resourcesSlideoutOpen}
        onClose={onResourcesClose}
        resources={resources}
        canManage={canManageResources}
        onAddResource={onAddResource}
        onEditResource={onEditResource}
        onDeleteResource={onDeleteResource}
      />
      <MembersSlideout
        open={membersSlideoutOpen}
        onClose={onMembersClose}
        members={members}
        requests={canManageMembers ? requests : []}
        canManage={canManageMembers}
        canSeeStatus={canSeeMemberStatus}
        isOwner={room.userIsOwner}
        onAddMember={onAddMember}
        onRemoveMember={onRemoveMember}
        onApproveMember={canManageMembers ? onApproveMember : undefined}
        onRejectMember={canManageMembers ? onRejectMember : undefined}
        onAddFacilitator={onAddFacilitator}
        onRemoveFacilitator={onRemoveFacilitator}
        onAddOwner={onAddOwner}
      />
      <ChatSlideout open={chatSlideoutOpen} onClose={onChatClose} roomId={room.id} memberCount={chatMemberCount} />
      <EventsSlideout
        open={eventsSlideoutOpen}
        onClose={onEventsClose}
        events={events}
        roomId={room.id}
        canManage={(room.userIsFacilitator || room.userIsOwner) && canCreateEvents}
        onAddEvent={canCreateEvents ? onAddEvent : undefined}
        bubbleSelectItems={bubbleSelectItems}
        defaultBubbleId={defaultBubbleId}
      />
    </Box>
  );
};
